import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Car, UserCheck, Users } from "lucide-react";
import { api } from "@/lib/api";
import { BrandLockup } from "@/components/brand";
import { Btn, Panel } from "@/components/kit";
import { formatReg } from "@/lib/vda";

// #2 Allocation resolution — a verified, legitimate driver of the vehicle's
// customer whose allocation data is imperfect can still proceed. Bad allocation
// data must never block a legitimate driver from requesting help.
const OPTIONS = [
  { mode: "mine", icon: Car, title: "This vehicle is allocated to me", hint: "Update my current allocation to this vehicle." },
  { mode: "temporary", icon: UserCheck, title: "I'm using this vehicle temporarily", hint: "Just for now — your primary allocation is kept." },
  { mode: "on_behalf", icon: Users, title: "I'm requesting for someone else", hint: "You're helping another driver with this vehicle." },
];

export default function DriverAllocationResolve() {
  const nav = useNavigate();
  const loc = useLocation();
  const vehicleId = loc.state?.vehicle_id;
  const reg = loc.state?.registration;
  const [busy, setBusy] = useState("");

  if (!vehicleId) { nav("/driver", { replace: true }); return null; }

  const choose = async (mode) => {
    setBusy(mode);
    try {
      await api.driverResolveAllocation(vehicleId, mode);
      toast.success("Thanks — you're all set.");
      nav("/driver/mileage", { replace: true });
    } catch (e) {
      toast.error(e.message || "Couldn't confirm that just now.");
    } finally { setBusy(""); }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-md px-5 py-8 vda-rise">
        <div className="mb-8"><BrandLockup /></div>
        <p className="vda-tick text-[hsl(var(--vda-clay))]">Confirm vehicle</p>
        <h1 className="text-2xl font-semibold tracking-tight mt-1">You're not currently shown as this vehicle's driver</h1>
        <p className="text-sm text-muted-foreground mt-2">
          We couldn't find a current allocation for <span className="vda-reg font-medium text-foreground">{formatReg(reg)}</span> to
          your details. That's fine — tell us how you're using it and we'll continue.
        </p>

        <div className="mt-6 space-y-3" data-testid="alloc-resolve-options">
          {OPTIONS.map((o) => {
            const Icon = o.icon;
            return (
              <Panel key={o.mode} className="p-0 overflow-hidden">
                <button
                  data-testid={`alloc-resolve-${o.mode}`}
                  onClick={() => choose(o.mode)}
                  disabled={!!busy}
                  className="w-full flex items-start gap-3 p-4 text-left hover:bg-secondary/40 disabled:opacity-50"
                >
                  <Icon className="h-5 w-5 mt-0.5 text-primary shrink-0" aria-hidden="true" />
                  <div>
                    <p className="font-medium text-foreground">{o.title}</p>
                    <p className="text-sm text-muted-foreground mt-0.5">{o.hint}</p>
                  </div>
                </button>
              </Panel>
            );
          })}
        </div>

        <p className="mt-6 text-xs text-muted-foreground leading-relaxed">
          We only allow this for vehicles within your own fleet. Your previous allocation history is
          always preserved.
        </p>
      </div>
    </div>
  );
}
